import { UserOccupationEnum } from '../enums/OccupationType';
import {
  Customer,
  EAdjudicationArea,
  EInvoice,
  ENotice,
  ENoticeDraft,
  EOrganization,
  EPublicNotice,
  ESnapshot
} from '.';
import { ConfirmationStatus } from '../enums';
import { ClassifiedLocation } from './classified';
import { Order } from './order';
import { PaperCheck } from './paperCheck';
import { NormalizedEntity } from './publicNotice';
import { PaymentMethods } from './invoices';
import { JobcaseSyncStatus } from './jobcase';
import { PublicationIssueSection } from './publicationIssueSection';
import { ExpressEmailConversation } from './expressEmailConversation';
import { SyncAttemptStatus } from './syncAttempt';

export const SORT_ASCENDING = 'asc';
export const SORT_DESCENDING = 'desc';
export type SORT_DIRECTIONS = typeof SORT_ASCENDING | typeof SORT_DESCENDING;

export const MAX_ELASTIC_FACETS_LENGTH = 250;

// Types of documents we sync to Elastic
export const ELASTIC_DOCUMENT_TYPES = [
  'notice',
  'draft',
  'customer',
  'organization',
  'publicNotice',
  'invoice',
  'order',
  'adjudicationArea',
  'publicationIssueSection',
  'expressEmailConversation'
] as const;
export type ElasticDocumentType = (typeof ELASTIC_DOCUMENT_TYPES)[number];

export const ELASTIC_SUBCOLLECTION_TYPES = ['publicationIssueSection'] as const;
export type ElasticSubcollectionTypes =
  (typeof ELASTIC_SUBCOLLECTION_TYPES)[number];
export type ElasticTopLevelCollectionTypes = Exclude<
  ElasticDocumentType,
  ElasticSubcollectionTypes
>;
export const isSubcollectionRefType = (
  type: ElasticDocumentType
): type is ElasticSubcollectionTypes => {
  return (ELASTIC_SUBCOLLECTION_TYPES as readonly string[]).includes(type);
};
export const isTopLevelCollectionRefType = (
  type: ElasticDocumentType
): type is ElasticTopLevelCollectionTypes => {
  return !isSubcollectionRefType(type);
};

export type SearchableNoticeRecord = {
  // configuration fields
  id: string;
  filerid: string;
  publisherid: string;
  draftownerid?: string;
  ispublisherdraft?: number;
  filerorganizationid?: string;
  firstpublicationdeadline: number;
  firstpublicationtimestamp: number;
  lastpublicationtimestamp: number;
  publicationtimestamps: number[];
  orginalnoticeid?: string;
  lasteditdate: number;
  affidavitdisabled: number;
  confirmedat: number;
  createtime: number;
  noticetype: number;

  // status fields
  requireupfrontpayment: number;
  affidavitsubmitted: number;
  iscancelled: number;
  confirmationstatus: ConfirmationStatus;
  isconfirmed: number;
  isarchived: number;
  hastransfer: number;
  isdraft: number;
  hasinvoice: number;
  advertiserbillingstatusvalue: number;
  publisherbillingstatusvalue: number;
  showaffidavitoutsidecolumn: number;
  postwithoutformatting: number;

  // TODO: to be removed after BACKEND-298 is complete
  advertiserbillingstatus: string;
  publisherbillingstatus: string;
  billingstatus: number;

  // searchable fields
  publicationdates: string;
  invoicenumber?: string;
  publication: string;
  noticename: string;
  filerorganizationname?: string;
  filername: string;
  fileremail: string;
  customid: string;
  purchaseordernumber?: string;
  noticetext: string;

  // extra fields
  filerphone?: string;

  noticedetailpath: string;
  generatedaffidaviturl: string;
  requireemailaffidavit: number;
  mailaffidavitsoutsidecolumn: number;
  failedsync: number;
  noticetypelabel: string;
  numberofcolumns: number;
  noticeheight: number;
  totalcolumninches: number;

  // extra fields -- pricing (from the invoice)
  publisheramountincents?: number;
  columnamountincents?: number;
  totalincents?: number;

  /** Sync status at deadline (if available) */
  syncstatusatdeadline?: number;
  syncfailurestatus?: number;

  /** Sync precondition status (if available) */
  syncpreconditionsuccess?: number;
  syncpreconditionfailurestatus?: number;

  /**
   * Whether or not Column is responsible for affidavit processing for this notice.
   * If true, Column will create an affidavit for this notice.
   */
  affidavitmanagedbycolumn: number;

  /**
   * Whether or not the publisher submitted the notice.
   *
   * NOTE: A reliable field only on notices created after ~December 2023
   */
  publishersubmitted?: number;
};

export type SearchableCustomerRecord = {
  // configuration fields
  id: string;
  organizationid: string;
  userid: string;
  verified: number;
  archived: number;

  /**
   * Determine the occupation of the user
   */
  useroccupation: UserOccupationEnum['value'];

  // searchable fields
  useremail: string;
  username: string;
  userorganizationname: string;
  internalid: string;

  // extra fields
  lastsignintime?: number;
};

export type SearchablePublicationIssueSectionRecord = {
  id: string;

  publicationissueid: string;

  publishername: string;
  publisherid: string;

  deadline: string | null;
  deadlinetimestamp: number | null;

  publicationdate: string;
  publicationtimestamp: number;

  status: string;
  type: string;
  publishingmedium: string;
  paginator: string;
};

export type SearchableOrganizationRecord = {
  // configuration fields
  id: string;
  organizationid: string;
  county: string;
  state: number;
  city: string;
  disabled: number;
  /**
   * The organization type (See OrganizationType enum for possible values)
   */
  type: number;
  /**
   * The organization status (See OrganizationStatus enum for possible values)
   */
  status: number;
  /**
   * Whether newspaper will appear for customers in the placement flow dropdown
   */
  isvalidpaper: number;
  /**
   * Whether the organization has active members or not
   */
  isactive: number;
  /**
   * Whether the advertiser organization appear in search when advertiser searches to join org
   */
  issearchable: number;
  isacceptingobituaries: number;
  isacceptingclassifieds: number;
  obituaryimplementationstatus?: string;
  classifiedimplementationstatus?: string;

  // searchable fields
  name: string;
  cities: string[];
  counties: string[];
  states: string[];

  // combine all of the searchable fields above into a single string that can be used for FTS
  location: string;

  // extra fields
  /**
   * The organization id of this organization's parent organization, if any
   */
  parentid?: string;

  stripeid?: string;

  /** Stripe customer ID from expressPlacementBilling config */
  expressbillingstripeid?: string;

  /** Stripe customer ID from payColumnInvoices config */
  monthlysubscriptionstripeid?: string;

  /** Shadow Stripe customer ID from payColumnInvoices config */
  monthlysubscriptionshadowstripeid?: string;
};

export type SearchableNewspaperOrderRecord = {
  // configuration fields

  // Newspaper Order Id
  id: string;
  orderid: string;
  publisherid: string;
  publishername: string;
  publisherlogo?: string;
  authorizedorgid?: string;
  authorizeduserid?: string;
  advertiseruserid?: string;
  advertiserorgid?: string;
  hasadvertiserorg: number;
  advertiserorgname?: string;
  ordertype: string;
  filingtype: string;
  publishingmedium: string;

  // whether the order comes from Column or is loaded from an external source
  external: number;

  // status fields
  newspaperorderstatus: string;
  invoicestatus?: number;
  orderstatus: string;
  hastransfer: number;
  /** Whether the newspaper order matches the current activeVersion property on the order */
  isactive: number;
  isverified?: number;

  // searchable fields
  funeralhomename?: string;
  /** Timestamp of the ad deadline */
  addeadline: number;
  publishingdates: string;
  publishingtimestamps: number[];
  deceasedname?: string;
  contactemail?: string;
  contactphone?: string;
  filerfirstname: string;
  filerlastname: string;
  content?: string;
  title?: string;

  // extra fields
  publishingdatesarray: string[];
  /** Midnight UTC timestamp of first pub date */
  firstpublishingdate: number;
  /** Midnight UTC timestamp of last pub date */
  lastpublishingdate: number;
  deceasedbirthdate?: number;
  deceaseddeathdate?: number;
  createdat: number;
  /** Midnight UTC timestamp of death verification date */
  verifiedat?: number;
  /** Midnight UTC timestamp of newspaper order status confirmation */
  confirmedat?: number;
  imagepaths: string[];
  proofstoragepath?: string;
  height?: number;
  width?: number;
  columns?: number;
  totalcolumninches?: number; // excludes offsets and roundoffs (bc this is being used by pagination teams to determine layout sizes)
  price?: number;
  locationzipcode?: ClassifiedLocation['zipCode'];
  locationcity?: ClassifiedLocation['city'];
  /** We convert the state enum value to the label when syncing to Elastic */
  locationstate?: string;
  publishersubmitted?: number;

  // Jobs fields
  linktoapplicationsite?: string;
  category?: string;
  travelrequirements?: string;
  employername?: string;
  jobcasesyncstatus?: JobcaseSyncStatus;

  // Search and metadata fields
  embedding: number[];

  // Pricing fields
  conveniencefeeincents?: number;
  conveniencefeesplitamountincents?: number;
  applicationfeeincents?: number;
  publisheramountincents?: number;
  subtotalincents?: number;
  totaldiscountincents?: number;
  taxesincents?: number;
  totalincents?: number;

  // Sync attempt status
  latestsyncattemptstatus?: SyncAttemptStatus;
  latestsyncattempttrigger?: string;
  latestsyncattemptmessages?: string[];
};

export type AdjucationAreaRecord = {
  // configuration fields
  id: string;
  name: string;
  fipsCode: string;
  type: string;
  parent: string;
};

export type ProductSpecificSearchableNewspaperOrderData = Pick<
  SearchableNewspaperOrderRecord,
  'content' | 'title' | 'createdat' | 'imagepaths' | 'proofstoragepath'
> & {
  isverified?: SearchableNewspaperOrderRecord['isverified'];
  deceaseddeathdate?: SearchableNewspaperOrderRecord['deceaseddeathdate'];
  deceasedbirthdate?: SearchableNewspaperOrderRecord['deceasedbirthdate'];
  deceasedname?: SearchableNewspaperOrderRecord['deceasedname'];
  funeralhomename?: SearchableNewspaperOrderRecord['funeralhomename'];
  verifiedat?: SearchableNewspaperOrderRecord['verifiedat'];
  price?: SearchableNewspaperOrderRecord['price'];
  locationzipcode?: SearchableNewspaperOrderRecord['locationzipcode'];
  locationcity?: SearchableNewspaperOrderRecord['locationcity'];
  locationstate?: SearchableNewspaperOrderRecord['locationstate'];
  linktoapplicationsite?: SearchableNewspaperOrderRecord['linktoapplicationsite'];
  category?: SearchableNewspaperOrderRecord['category'];
  travelrequirements?: SearchableNewspaperOrderRecord['travelrequirements'];
  employername?: SearchableNewspaperOrderRecord['employername'];
};

// Type of public notice record sent to Elastic
export type ElasticSearchablePublicNoticeRecord = {
  // configuration fields
  id: string;
  publishedtimestamp?: number;

  // searchable fields
  text: string;
  noticetype: string;
  newspapername: string;
  county: string;
  state: string;

  // extra fields
  // JSON stringified object or an array of normalized entities
  normalizedentities?: string;
  geoloc?: {
    lat: number;
    lng: number;
  };
  pdfurl: string;
  /** Shows both text and PDF content when avaialable */
  combo?: number;
  filer?: string;
};

// Type of public notice record received from Elastic
export type SearchablePublicNoticeRecord = Omit<
  ElasticSearchablePublicNoticeRecord,
  'normalizedentities'
> & {
  normalizedentities?: NormalizedEntity | NormalizedEntity[];
  highlighted_text?: string;
};

export type SearchableInvoiceRecord = {
  // configuration fields
  id: string;
  filerid: string;
  publisherid: string;
  isbulkinvoice: number;
  filerorganizationid?: string;

  // searchable fields
  filername: string;
  fileremail: string;
  filerphone: string;
  invoicenumber: string;
  formattedduedate: string;
  formattednettotal: string;
  lineitemdescriptions: string[];
  filerorganizationname: string;
  publishername: string;
  noticename?: string;
  purchaseordernumber?: string;
  noticeid?: string;
  stripecustomerid: string; // cus_ stripe id
  stripeinvoiceid: string; // inv_ stripe id
  stripecheckids?: string[]; // pchk_ ids, empty array if no checks
  checknumbers?: string[]; // check numbers, empty array if no checks
  checkpayers?: string[]; // check payers, empty array if no checks
  paymentmethod?: PaymentMethods; // payment method used to pay the invoice

  // extra fields
  invoiceoutsidecolumn: number;
  voided: number;
  status: number;
  paid: number;
  paidat: number | null; // timestamp of payment
  created: number;
  duedate: number;
  nettotal: number;
  manualpaymentnote: string;
};

/**
 * A searchable invoice record with a check object attached.  Mainly used for check reconciliation in admin.
 * Assuming a single check per invoice for now, as our processes don't support multiple checks per invoice.
 */
export type SearchableInvoiceRecordWithCheck = SearchableInvoiceRecord & {
  papercheck?: PaperCheck;
};

export type SearchableAdjudicationAreaRecord = {
  // configuration fields
  id: string;
  name: string;
  fipscode: string;
  type: string;
  parentid: string;
  parentname: string;
  parentfipscode: string;
};

export type SearchableExpressEmailConversationRecord = {
  id: string;
  conversation_id: string;
  conversation_subject: string;
  conversation_created_at: string;
  notice_id: string;
  notice_status: number;
  invoice_status: number;
  invoice_require_upfront_payment: number;
  invoice_paid: number;
  running: number;
  organizationid: string;
  lastpublicationtimestamp: number;
  conversationtype: string;
  external_id: string;
  custom_id: string;
};

export type SearchableRecord =
  | SearchableNoticeRecord
  | SearchableCustomerRecord
  | SearchableOrganizationRecord
  | ElasticSearchablePublicNoticeRecord
  | SearchablePublicNoticeRecord
  | SearchableInvoiceRecord
  | SearchableNewspaperOrderRecord
  | SearchableAdjudicationAreaRecord
  | SearchablePublicationIssueSectionRecord
  | SearchableExpressEmailConversationRecord;

export const isSearchableCustomerRecord = (
  record: SearchableRecord
): record is SearchableCustomerRecord => {
  const requiredFields: (keyof SearchableCustomerRecord)[] = [
    'userid',
    'organizationid',
    'verified'
  ];
  if (
    requiredFields.every(
      requiredField =>
        (record as SearchableCustomerRecord)[requiredField] !== undefined
    )
  ) {
    return true;
  }
  return false;
};

export const isSearchableNewspaperOrderRecord = (
  record: SearchableRecord
): record is SearchableNewspaperOrderRecord => {
  const requiredFields: (keyof SearchableNewspaperOrderRecord)[] = [
    'orderid',
    'publisherid',
    'newspaperorderstatus'
  ];
  if (
    requiredFields.every(
      requiredField =>
        (record as SearchableNewspaperOrderRecord)[requiredField] !== undefined
    )
  ) {
    return true;
  }
  return false;
};

export type ElasticAggregationBucket = {
  key: string | number;
  doc_count: number;
  // Nested aggregations or metric results
  [aggName: string]:
    | ElasticAggregationResult
    | { value: number }
    | string
    | number
    | undefined;
};

export type ElasticAggregationResult = {
  buckets: ElasticAggregationBucket[];
};

export type ElasticAggregations = {
  [aggName: string]: ElasticAggregationResult;
};

/**
 * Type guard to check if a value is an ElasticAggregationResult
 */
export const isElasticAggregationResult = (
  value: unknown
): value is ElasticAggregationResult => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'buckets' in value &&
    Array.isArray((value as ElasticAggregationResult).buckets)
  );
};

// https://www.elastic.co/guide/en/app-search/8.8/api-reference.html
type ElasticSearchResultsBase = {
  results: SearchableRecord[];
  page: {
    current: number;
    total_pages: number;
    total_results: number;
    size: number;
  };
  aggregations?: ElasticAggregations;
};

export type ElasticCustomerSearchResults = ElasticSearchResultsBase & {
  results: SearchableCustomerRecord[];
};

export type ElasticOrderSearchResults = ElasticSearchResultsBase & {
  results: SearchableNewspaperOrderRecord[];
};

export type ElasticNoticeSearchResults = ElasticSearchResultsBase & {
  results: SearchableNoticeRecord[];
};

export type ElasticOrganizationSearchResults = ElasticSearchResultsBase & {
  results: SearchableOrganizationRecord[];
};

export type ElasticPublicNoticeSearchResults = ElasticSearchResultsBase & {
  results: SearchablePublicNoticeRecord[];
};

export type ElasticInvoiceSearchResults = ElasticSearchResultsBase & {
  results: SearchableInvoiceRecord[];
};

export type ElasticAdjudicationAreaSearchResults = ElasticSearchResultsBase & {
  results: SearchableAdjudicationAreaRecord[];
};

export type ElasticSearchResults =
  | ElasticCustomerSearchResults
  | ElasticNoticeSearchResults
  | ElasticOrganizationSearchResults
  | ElasticPublicNoticeSearchResults
  | ElasticInvoiceSearchResults
  | ElasticOrderSearchResults
  | ElasticAdjudicationAreaSearchResults;

export type FacetData = {
  value: string | number;
  count: number;
};

export type FacetResults = {
  type: 'value' | 'range';
  name: string;
  data: FacetData[];
};

export type ElasticPublicNoticeFacetsResults = {
  [K in keyof SearchablePublicNoticeRecord]: FacetResults[];
};

export type ElasticNoticeFacetsResults = {
  [K in keyof SearchableNoticeRecord]: FacetResults[];
};

export type ElasticOrganizationFacetsResults = {
  [K in keyof SearchableOrganizationRecord]: FacetResults[];
};

export type ElasticCustomerFacetsResults = {
  [K in keyof SearchableCustomerRecord]: FacetResults[];
};

export type ElasticOrderFacetsResults = {
  [K in keyof SearchableNewspaperOrderRecord]: FacetResults[];
};

export type ElasticAdjudicationAreaFacetsResults = {
  [K in keyof AdjucationAreaRecord]: FacetResults[];
};

export type ElasticFacetsResults =
  | ElasticPublicNoticeFacetsResults
  | ElasticNoticeFacetsResults
  | ElasticOrganizationFacetsResults
  | ElasticCustomerFacetsResults
  | ElasticOrderFacetsResults
  | ElasticAdjudicationAreaFacetsResults;

type ValueOrArray<T> = T | T[] | [null];

/**
 * See: https://www.elastic.co/guide/en/app-search/current/filters.html#filters-value-filters
 */
export type SearchableNoticeRecordValueFilter = Partial<{
  [K in keyof SearchableNoticeRecord]: ValueOrArray<SearchableNoticeRecord[K]>;
}>;

type SearchableValue<K extends keyof SearchableNoticeRecord> =
  SearchableNoticeRecord[K] extends Array<infer T>
    ? T
    : SearchableNoticeRecord[K];

type SearchableNewspaperOrderValue<
  K extends keyof SearchableNewspaperOrderRecord
> = SearchableNewspaperOrderRecord[K] extends Array<infer T>
  ? T
  : SearchableNewspaperOrderRecord[K];

/**
 * See:
 * https://www.elastic.co/guide/en/app-search/current/filters.html#filters-range-filters
 */
export type SearchableNoticeRecordRangeFilter = Partial<{
  [K in keyof SearchableNoticeRecord]: {
    from?: SearchableValue<K>;
    to?: SearchableValue<K>;
  };
}>;

export type SearchableNoticeRecordFilter =
  | SearchableNoticeRecordValueFilter
  | SearchableNoticeRecordRangeFilter;

export type SearchableOrganizationRecordFilter = Partial<{
  [K in keyof SearchableOrganizationRecord]: ValueOrArray<
    SearchableOrganizationRecord[K]
  >;
}>;

export type SearchableCustomerRecordFilter = Partial<{
  [K in keyof SearchableCustomerRecord]: ValueOrArray<
    SearchableCustomerRecord[K]
  >;
}>;

type SearchableNewspaperOrderRecordValueFilter = Partial<{
  [K in keyof SearchableNewspaperOrderRecord]: ValueOrArray<
    SearchableNewspaperOrderRecord[K]
  >;
}>;

type SearchableNewspaperOrderRecordRangeFilter = Partial<{
  [K in keyof SearchableNewspaperOrderRecord]: {
    from?: SearchableNewspaperOrderValue<K>;
    to?: SearchableNewspaperOrderValue<K>;
  };
}>;

export type SearchableNewspaperOrderRecordFilter =
  | SearchableNewspaperOrderRecordValueFilter
  | SearchableNewspaperOrderRecordRangeFilter;

export type SearchableInvoiceRecordValueFilter = Partial<{
  [K in keyof SearchableInvoiceRecord]: ValueOrArray<
    SearchableInvoiceRecord[K]
  >;
}>;

export type SearchableInvoiceRecordRangeFilter = Partial<{
  [K in keyof SearchableInvoiceRecord]: {
    from?: SearchableInvoiceRecord[K];
    to?: SearchableInvoiceRecord[K];
  };
}>;

export type SearchableInvoiceRecordSimpleFilter =
  | SearchableInvoiceRecordValueFilter
  | SearchableInvoiceRecordRangeFilter;

export type SearchableInvoiceRecordFilter =
  | SearchableInvoiceRecordSimpleFilter
  | {
      all?: SearchableInvoiceRecordSimpleFilter[];
      any?: SearchableInvoiceRecordSimpleFilter[];
      none?: SearchableInvoiceRecordSimpleFilter[];
    };

export type SearchablePublicNoticeRecordValueFilter = Partial<{
  [K in keyof SearchablePublicNoticeRecord]: ValueOrArray<
    SearchablePublicNoticeRecord[K]
  >;
}>;

export type SearchablePublicNoticeRecordRangeFilter = Partial<{
  [K in keyof SearchablePublicNoticeRecord]: {
    from?: SearchablePublicNoticeRecord[K];
    to?: SearchablePublicNoticeRecord[K];
  };
}>;

export type SearchablePublicNoticeRecordValueOrRangeFilter =
  | SearchablePublicNoticeRecordValueFilter
  | SearchablePublicNoticeRecordRangeFilter;

export type SearchablePublicNoticeRecordFilter =
  | SearchablePublicNoticeRecordValueOrRangeFilter
  | {
      all?: SearchablePublicNoticeRecordValueOrRangeFilter[];
      any?: SearchablePublicNoticeRecordValueOrRangeFilter[];
      none?: SearchablePublicNoticeRecordValueOrRangeFilter[];
    };

export type SearchableAdjudicationAreaRecordFilter = Partial<{
  [K in keyof SearchableAdjudicationAreaRecord]: ValueOrArray<
    SearchableAdjudicationAreaRecord[K]
  >;
}>;

export type SearchablePublicationIssueSectionRecordValueFilter = Partial<{
  [K in keyof SearchablePublicationIssueSectionRecord]: ValueOrArray<
    SearchablePublicationIssueSectionRecord[K]
  >;
}>;

export type SearchablePublicationIssueSectionRecordRangeFilter = Partial<{
  [K in keyof SearchablePublicationIssueSectionRecord]: {
    from?: SearchablePublicationIssueSectionRecord[K];
    to?: SearchablePublicationIssueSectionRecord[K];
  };
}>;

export type SearchablePublicationIssueSectionRecordValueOrRangeFilter =
  | SearchablePublicationIssueSectionRecordValueFilter
  | SearchablePublicationIssueSectionRecordRangeFilter;

export type SearchablePublicationIssueSectionRecordFilter =
  | SearchablePublicationIssueSectionRecordValueOrRangeFilter
  | {
      all?: SearchablePublicationIssueSectionRecordValueOrRangeFilter[];
      any?: SearchablePublicationIssueSectionRecordValueOrRangeFilter[];
      none?: SearchablePublicationIssueSectionRecordValueOrRangeFilter[];
    };

export type SearchableExpressEmailConversationRecordFilter = Partial<{
  [K in keyof SearchableExpressEmailConversationRecord]: ValueOrArray<
    SearchableExpressEmailConversationRecord[K]
  >;
}>;

export type SearchableRecordFilter =
  | SearchableCustomerRecordFilter
  | SearchableOrganizationRecordFilter
  | SearchableNoticeRecordFilter
  | SearchablePublicNoticeRecordFilter
  | SearchableNewspaperOrderRecordFilter
  | SearchableAdjudicationAreaRecordFilter
  | SearchablePublicationIssueSectionRecordFilter
  | SearchableExpressEmailConversationRecordFilter;

type Facet = {
  type: 'value' | 'range';
  name: string;
  size?: number; // Maximum is 250
  sort?: { value: 'desc' | 'asc' };
  ranges?: { from: number; to: number }[];
};

export type SearchablePublicNoticeRecordFacet = Partial<{
  [K in keyof SearchablePublicNoticeRecord]: Facet;
}>;

export type SearchableNoticeRecordFacet = Partial<{
  [K in keyof SearchableNoticeRecord]: Facet;
}>;

export type SearchableOrganizationRecordFacet = Partial<{
  [K in keyof SearchableOrganizationRecord]: Facet;
}>;

export type SearchableCustomerRecordFacet = Partial<{
  [K in keyof SearchableCustomerRecord]: Facet;
}>;

export type SearchableNewspaperOrderRecordFacet = Partial<{
  [K in keyof SearchableNewspaperOrderRecord]: Facet;
}>;

export type SearchableRecordFacet =
  | SearchableCustomerRecordFacet
  | SearchableOrganizationRecordFacet
  | SearchableNoticeRecordFacet
  | SearchablePublicNoticeRecordFacet
  | SearchableNewspaperOrderRecordFacet;

export type ElasticDocumentSnapshot = ESnapshot<
  | ENotice
  | ENoticeDraft
  | Customer
  | EOrganization
  | EPublicNotice
  | EInvoice
  | Order
  | EAdjudicationArea
  | PublicationIssueSection
  | ExpressEmailConversation
>;

export type ElasticParamsFilters<T extends SearchableRecordFilter> = {
  all: T[];
  any?: T[];
  none?: T[];
};

// Define a recursive AggregationParam type for unlimited nested aggregations
// see https://www.elastic.co/docs/explore-analyze/query-filter/aggregations
export type AggregationParam = {
  [key: string]: {
    terms?: {
      field?: string;
      size?: number;
      missing?: string;
      script?: {
        source: string;
      };
    };
    sum?: {
      field: string;
    };
    avg?: {
      field: string;
    };
    min?: {
      field: string;
    };
    max?: {
      field: string;
    };
    date_histogram?: {
      field: string;
      calendar_interval: string;
      min_doc_count?: number;
      order?: { _key: 'desc' | 'asc' };
    };
    top_hits?: {
      size: number;
      _source?: {
        includes?: string[];
      };
    };
    aggs?: AggregationParam;
  };
};

// TODO: consider using official SearchRequest Elasticsearch type here; right now we are using a custom type
export type ElasticParams<
  T extends SearchableRecordFilter = SearchableRecordFilter
> = {
  filters: ElasticParamsFilters<T>;
  page: {
    size: number;
    current?: number;
  };
  sort?: Record<string, SORT_DIRECTIONS>[];
  result_fields?: Record<
    keyof T,
    { snippet?: { size: number }; raw: Record<string, unknown> }
  >;
  search_fields?: Record<string, { weight: number }>;
  // We query for aggregations using the multi-field approach (see https://www.elastic.co/docs/reference/elasticsearch/mapping-reference/multi-fields)
  // aggs are usable only with Elasticsearch (and not with App Search or Enterprise Search) and fields on which we aggregate must be correctly mapped
  aggs?: AggregationParam;
};
